import React, { useState } from 'react';
import { useLogoGenerator } from '../../hooks/logo/useLogoGenerator';
import LogoPreview from './LogoPreview';
import LogoControls from './LogoControls';
import LogoTextInput from './LogoTextInput';
import LogoOptions from './LogoOptions';
import BackgroundGenerator from './BackgroundGenerator';

const LogoGenerator: React.FC = () => {
  const [text, setText] = useState('ScaleNix');
  const [subtext, setSubtext] = useState('Open Source Solutions');
  const [isTransparent, setIsTransparent] = useState(true);
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#4F46E5');
  const [textColor, setTextColor] = useState('#1E293B');
  const [subtextColor, setSubtextColor] = useState('#6B7280');
  const [size, setSize] = useState(64);
  const [gradient, setGradient] = useState(true);
  const [font, setFont] = useState('Inter');
  const [subtextFont, setSubtextFont] = useState('Inter');
  const [iconSize, setIconSize] = useState(32);
  const [spacing, setSpacing] = useState(12);
  const [subtextSpacing, setSubtextSpacing] = useState(4);
  const [fontWeight, setFontWeight] = useState(700);
  const [subtextWeight, setSubtextWeight] = useState(500);
  const { handleExport, isExporting } = useLogoGenerator();

  const fonts = [
    'Inter',
    'Arial',
    'Helvetica',
    'Roboto',
    'Open Sans',
    'Montserrat',
    'Poppins'
  ];

  const weights = [
    { value: 400, label: 'Regular' },
    { value: 500, label: 'Medium' },
    { value: 600, label: 'Semibold' },
    { value: 700, label: 'Bold' },
    { value: 800, label: 'Extrabold' }
  ];

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          ScaleNix Logo Generator
        </h1>
        
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="space-y-6">
            {/* Main Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Text
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Subtext */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtext
              </label>
              <input
                type="text"
                value={subtext}
                onChange={(e) => setSubtext(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Font Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Font
                </label>
                <select
                  value={font}
                  onChange={(e) => setFont(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {fonts.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtext Font
                </label>
                <select
                  value={subtextFont}
                  onChange={(e) => setSubtextFont(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {fonts.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Font Weights */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Font Weight
                </label>
                <select
                  value={fontWeight}
                  onChange={(e) => setFontWeight(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {weights.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtext Font Weight
                </label>
                <select
                  value={subtextWeight}
                  onChange={(e) => setSubtextWeight(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {weights.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="h-8 w-8 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="h-8 w-8 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="h-8 w-8 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="flex-1 px-3 py-1 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Sizes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon Size
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="16"
                    max="64"
                    value={iconSize}
                    onChange={(e) => setIconSize(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 w-12">{iconSize}px</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Text Spacing
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="4"
                    max="24"
                    value={spacing}
                    onChange={(e) => setSpacing(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 w-12">{spacing}px</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtext Spacing
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="2"
                    max="12"
                    value={subtextSpacing}
                    onChange={(e) => setSubtextSpacing(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 w-12">{subtextSpacing}px</span>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="transparent"
                  checked={isTransparent}
                  onChange={(e) => setIsTransparent(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="transparent" className="text-sm font-medium text-gray-700">
                  Transparent Background
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="gradient"
                  checked={gradient}
                  onChange={(e) => setGradient(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="gradient" className="text-sm font-medium text-gray-700">
                  Use Gradient
                </label>
              </div>
            </div>

            {/* Preview */}
            <div className="relative overflow-hidden rounded-lg border border-gray-200">
              <div 
                id="logo-preview"
                className={`p-8 flex items-center justify-center ${
                  isTransparent ? 'bg-transparent' : 'bg-white'
                }`}
                style={{
                  backgroundImage: isTransparent ? `
                    linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                  ` : undefined,
                  backgroundSize: '20px 20px',
                  backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                }}
              >
                <div className="flex items-center">
                  {/* Icon */}
                  <div className="relative" style={{ width: iconSize, height: iconSize }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`text-${gradient ? 'transparent' : primaryColor}`}>
                      <defs>
                        <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor={primaryColor} />
                          <stop offset="100%" stopColor={secondaryColor} />
                        </linearGradient>
                      </defs>
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        stroke={gradient ? 'url(#iconGradient)' : 'currentColor'}
                      />
                    </svg>
                    <div className="absolute -right-1 -top-1 h-1.5 w-1.5 bg-blue-500 rounded-full" />
                    <div className="absolute -left-1 -bottom-1 h-1.5 w-1.5 bg-indigo-500 rounded-full" />
                  </div>

                  {/* Text */}
                  <div style={{ marginLeft: spacing }}>
                    <div 
                      style={{ 
                        fontFamily: font,
                        fontWeight: fontWeight,
                        fontSize: size,
                        color: textColor,
                        background: gradient ? `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` : 'none',
                        WebkitBackgroundClip: gradient ? 'text' : 'none',
                        WebkitTextFillColor: gradient ? 'transparent' : 'currentColor'
                      }}
                    >
                      {text}
                    </div>
                    <div 
                      style={{ 
                        fontFamily: subtextFont,
                        fontWeight: subtextWeight,
                        fontSize: size * 0.4,
                        marginTop: subtextSpacing,
                        color: subtextColor
                      }}
                    >
                      {subtext}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleExport('png')}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isExporting ? 'Exporting...' : 'Export PNG'}
              </button>
              <button
                onClick={() => handleExport('svg')}
                disabled={isExporting}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
              >
                {isExporting ? 'Exporting...' : 'Export SVG'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoGenerator;